import type { LinkMapScope } from '../../types';

/**
 * One colour per SOURCE in a link map — used on the scope chips, the tree's
 * source headers, and every page row under them.
 *
 * The colour is the whole point of the merged tree. Pages from three subjects
 * on two sites are one flat list otherwise, and "which of these is from my bike
 * site" becomes something you read rather than something you see.
 *
 * Copied from `boardroom/seatColors` rather than shared with it, deliberately. They
 * answer different questions (which voice said this / which source is this
 * from), and a shared palette would mean a change made for the boardroom
 * silently recolours Link Builder.
 *
 * RED AND AMBER ARE MISSING ON PURPOSE — here they carry state meanings that a
 * source colour must not collide with: red is "this target has never been
 * published, so the link will be dropped", amber is "out of scope".
 *
 * Tailwind purges dynamically-built class names, so these are whole literal
 * strings rather than `bg-${hue}-500`.
 */

export interface SourceColor {
  /** Text, for the source header and the chip label. */
  text: string;
  /** A solid dot or swatch. */
  dot: string;
  /** Left rail on a page row, so a scan down the tree reads as bands. */
  border: string;
  /** Chip background. */
  soft: string;
}

const PALETTE: SourceColor[] = [
  {
    text: 'text-indigo-700 dark:text-indigo-300',
    dot: 'bg-indigo-500',
    border: 'border-l-indigo-400',
    soft: 'bg-indigo-50 dark:bg-indigo-900/30',
  },
  {
    text: 'text-teal-700 dark:text-teal-300',
    dot: 'bg-teal-500',
    border: 'border-l-teal-400',
    soft: 'bg-teal-50 dark:bg-teal-900/30',
  },
  {
    text: 'text-fuchsia-700 dark:text-fuchsia-300',
    dot: 'bg-fuchsia-500',
    border: 'border-l-fuchsia-400',
    soft: 'bg-fuchsia-50 dark:bg-fuchsia-900/30',
  },
  {
    text: 'text-cyan-700 dark:text-cyan-300',
    dot: 'bg-cyan-500',
    border: 'border-l-cyan-400',
    soft: 'bg-cyan-50 dark:bg-cyan-900/30',
  },
  {
    text: 'text-violet-700 dark:text-violet-300',
    dot: 'bg-violet-500',
    border: 'border-l-violet-400',
    soft: 'bg-violet-50 dark:bg-violet-900/30',
  },
  {
    text: 'text-lime-700 dark:text-lime-300',
    dot: 'bg-lime-500',
    border: 'border-l-lime-400',
    soft: 'bg-lime-50 dark:bg-lime-900/30',
  },
];

export const NEUTRAL: SourceColor = {
  text: 'text-gray-700 dark:text-gray-300',
  dot: 'bg-gray-400',
  border: 'border-l-gray-300',
  soft: 'bg-gray-100 dark:bg-gray-800',
};

/**
 * Colour by the scope's own `order`, NOT by its position in this array and NOT
 * by its id.
 *
 * `order` is assigned max+1 on the server and never renumbered, which is what
 * makes the colour stable: removing the second of four sources must not shift
 * the colours of the two below it. Keying on array position would do exactly
 * that, and a colour key that reshuffles is one nobody can learn.
 */
export function sourceColors(scopes: LinkMapScope[]): Map<number, SourceColor> {
  const out = new Map<number, SourceColor>();
  for (const scope of scopes) {
    out.set(scope.id, PALETTE[scope.order % PALETTE.length]);
  }
  return out;
}

/** The colour a source WOULD get if added next — for the picker, so the chip
 *  already wears its colour before the merged tree is ever seen. */
export function nextSourceColor(scopes: LinkMapScope[]): SourceColor {
  const nextOrder = scopes.reduce((max, s) => Math.max(max, s.order), -1) + 1;
  return PALETTE[nextOrder % PALETTE.length];
}

/** How a source names itself: the subject, or the branch within it. */
export function sourceLabel(scope: LinkMapScope): string {
  return scope.root_topic_name || scope.project_name;
}

/** The second line — only when the label above isn't already the whole story. */
export function sourceSublabel(scope: LinkMapScope): string | null {
  return scope.root_topic_name ? scope.project_name : null;
}

export const PALETTE_SIZE = PALETTE.length;
